import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Modal,
  Button,
  Group,
  TextInput,
  Box,
  Select,
  InputBase,
  Autocomplete,
  Loader,
  Alert,
  Text,
  Paper,
  Stack,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconChevronDown, IconCheck, IconAlertCircle, IconMapPin, IconX } from '@tabler/icons-react'
import type { Patient, Practitioner } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { USPhoneInput } from '@/components/ui/USPhoneInput'
import { IMaskInput } from 'react-imask'

import { states } from '@/utils/states'
import { useMiddlewareClient } from '@/hooks'

// ── NPI Registry API (proxied through middleware to avoid CORS) ───────────────

type NpiResult = {
  enumeration_type: string
  number: string
  basic: {
    first_name?: string
    last_name?: string
    middle_name?: string
    name_prefix?: string
    credential?: string
    status: string
  }
  addresses: Array<{
    address_purpose: string
    address_1: string
    address_2?: string
    city: string
    state: string
    postal_code: string
    telephone_number?: string
  }>
  taxonomies: Array<{
    desc: string
    primary: boolean
  }>
}

type NpiApiResponse = { result_count: number; results: NpiResult[] }
type MiddlewareClient = ReturnType<typeof useMiddlewareClient>

type UpdatePatientPcpPayload = {
  patientId: string
  npi?: string
  firstName: string
  lastName: string
  phone?: string
  addressLine?: string
  addressCity?: string
  addressState?: string
  addressPostalCode?: string
}

async function lookupByNpi(client: MiddlewareClient, npi: string): Promise<NpiResult | null> {
  const data = await (client as unknown as { get: (url: string) => Promise<NpiApiResponse> }).get(
    `/npi/lookup?number=${npi}`,
  )
  return data.result_count > 0 ? (data.results[0] ?? null) : null
}

async function searchByName(client: MiddlewareClient, query: string): Promise<NpiResult[]> {
  const data = await (client as unknown as { get: (url: string) => Promise<NpiApiResponse> }).get(
    `/npi/search?name=${encodeURIComponent(query)}`,
  )
  return data.results ?? []
}

async function updatePatientPcp(
  client: MiddlewareClient,
  payload: UpdatePatientPcpPayload,
): Promise<{ linkedToExisting?: boolean }> {
  return await (
    client as unknown as {
      put: (url: string, body: UpdatePatientPcpPayload) => Promise<{ linkedToExisting?: boolean }>
    }
  ).put('/patient/{patientId}/pcp', payload)
}

function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

function npiPhoneToE164(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return phone
}

function formatNpiPostalCode(postal: string): string {
  const digits = postal.replace(/\D/g, '')
  if (digits.length >= 9) return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`
  return digits.slice(0, 5)
}

// ── Validation ────────────────────────────────────────────────────────────────

const NAME_REGEX = /^[a-zA-Z\s'-]+$/
const ADDRESS_CODE_REGEX = /[<>{}[\]]/

function validateName(v: string, label: string): string | null {
  const t = v.trim()
  if (!t) return `${label} is required`
  if (t.length < 2) return `${label} must be at least 2 characters`
  if (!NAME_REGEX.test(t))
    return `${label} can only include letters, spaces, apostrophes, and hyphens`
  return null
}

function validatePhone(v: string): string | null {
  if (!v) return 'Phone number is required'
  try {
    if (!isValidPhoneNumber(v)) return 'Invalid phone number'
  } catch {
    return 'Invalid phone number'
  }
  return null
}

function validateAddressLine(v: string): string | null {
  const t = v.trim()
  if (!t) return 'Address is required'
  if (t.length < 3) return 'Address must be at least 3 characters'
  if (ADDRESS_CODE_REGEX.test(v)) return 'Address cannot contain code or script characters'
  return null
}

function validateCity(v: string): string | null {
  const t = v.trim()
  if (!t) return 'City is required'
  if (t.length < 3) return 'City must be at least 3 letters'
  if (ADDRESS_CODE_REGEX.test(t)) return 'City cannot contain code or script characters'
  return null
}

function validateState(v: string): string | null {
  return v ? null : 'State is required'
}

function validateZip(v: string): string | null {
  const digits = v.replace(/\D/g, '')
  if (!digits) return 'ZIP code is required'
  if (digits.length !== 5 && digits.length !== 9) return 'ZIP code must be 5 or 9 digits'
  return null
}

// ── Form types ────────────────────────────────────────────────────────────────

type FormFields = {
  firstName: string
  lastName: string
  phone: string
  addressLine: string
  addressCity: string
  addressState: string
  addressPostalCode: string
}

function npiResultToFields(result: NpiResult): FormFields & { npi: string } {
  const loc = result.addresses.find((a) => a.address_purpose === 'LOCATION') ?? result.addresses[0]
  return {
    npi: result.number,
    firstName: toTitleCase(result.basic.first_name ?? ''),
    lastName: toTitleCase(result.basic.last_name ?? ''),
    phone: loc?.telephone_number ? npiPhoneToE164(loc.telephone_number) : '',
    addressLine: [loc?.address_1, loc?.address_2].filter(Boolean).join(' '),
    addressCity: toTitleCase(loc?.city ?? ''),
    addressState: loc?.state ?? '',
    addressPostalCode: loc?.postal_code ? formatNpiPostalCode(loc.postal_code) : '',
  }
}

function npiResultLabel(result: NpiResult): string {
  const name = [result.basic.first_name, result.basic.last_name]
    .filter((value): value is string => Boolean(value))
    .map(toTitleCase)
    .join(' ')
  const credential = result.basic.credential ? `, ${result.basic.credential}` : ''
  const specialty = result.taxonomies.find((t) => t.primary)?.desc ?? ''
  return `${name}${credential}${specialty ? ` · ${specialty}` : ''}`
}

// ── Address autocomplete ──────────────────────────────────────────────────────

interface AddressSuggestion {
  street_line: string
  secondary?: string
  city: string
  state: string
  zipcode?: string
  entries?: number
}

function getAddressSuggestionValue(suggestion: AddressSuggestion): string {
  return `${suggestion.street_line}|${suggestion.city}|${suggestion.state}|${suggestion.zipcode ?? ''}`
}

function getAddressSuggestionLabel(suggestion: AddressSuggestion): string {
  return [
    suggestion.street_line,
    suggestion.secondary,
    suggestion.city,
    suggestion.state,
    suggestion.zipcode,
  ]
    .filter(Boolean)
    .join(', ')
}

function AddressAutocomplete({
  label,
  value,
  onChange,
  onAddressSelect,
  error,
  onBlur,
  placeholder = '123 Highland Rd',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  onAddressSelect: (s: AddressSuggestion) => void
  error?: string | null
  onBlur?: () => void
  placeholder?: string
}) {
  const middleware = useMiddlewareClient()
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [debouncedValue] = useDebouncedValue(value, 300)
  const isSelectingRef = useRef(false)
  const shouldSearchRef = useRef(false)

  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query || query.length < 3 || query.length > 100) {
        setSuggestions([])
        return
      }
      setLoading(true)
      try {
        const client = middleware as unknown as {
          get: (url: string) => Promise<{ suggestions: AddressSuggestion[] }>
        }
        const data = await client.get(`/address/autocomplete?query=${encodeURIComponent(query)}`)
        setSuggestions(data?.suggestions ?? [])
      } catch {
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    },
    [middleware],
  )

  useEffect(() => {
    if (shouldSearchRef.current && !isSelectingRef.current) {
      searchAddresses(debouncedValue)
      shouldSearchRef.current = false
    }
  }, [debouncedValue, searchAddresses])

  const autocompleteOptions = Array.from(
    new Map(
      suggestions.map((s) => {
        const optionValue = getAddressSuggestionValue(s)
        const optionLabel = getAddressSuggestionLabel(s)

        return [
          optionValue,
          {
            value: optionValue,
            label: optionLabel,
          },
        ]
      }),
    ).values(),
  )

  return (
    <Autocomplete
      label={<span className="line-[1.4] text-[13px] text-[#7D7987]">{label}</span>}
      placeholder={placeholder}
      value={value}
      filter={(input) => input.options}
      error={error}
      onChange={(v) => {
        if (isSelectingRef.current) {
          isSelectingRef.current = false
          return
        }
        shouldSearchRef.current = true
        onChange(v)
      }}
      onBlur={onBlur}
      onOptionSubmit={(selected) => {
        isSelectingRef.current = true
        const suggestion = suggestions.find((s) => getAddressSuggestionValue(s) === selected)
        if (suggestion) {
          onAddressSelect(suggestion)
          setSuggestions([])
        }
      }}
      data={autocompleteOptions}
      rightSection={
        loading ? <Loader size={16} /> : <IconMapPin size={16} className="text-gray-400" />
      }
      classNames={{ root: 'w-full', input: 'h-[36px] border border-slate-300' }}
    />
  )
}
// ── Modal ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean
  onClose: () => void
  patient: Patient
  existingPractitioner: Practitioner | null
  onSuccess: () => void
}

const ALL_TOUCHED = {
  firstName: true,
  lastName: true,
  phone: true,
  addressLine: true,
  addressCity: true,
  addressState: true,
  addressPostalCode: true,
}

export function PCPModal({ isOpen, onClose, patient, existingPractitioner, onSuccess }: Props) {
  const middleware = useMiddlewareClient()

  // Lookup state
  const [npi, setNpi] = useState('')
  const [npiStatus, setNpiStatus] = useState<
    'idle' | 'loading' | 'found' | 'not-found' | 'inactive' | 'error'
  >('idle')
  const [npiFoundLabel, setNpiFoundLabel] = useState('')
  const [nameSearch, setNameSearch] = useState('')
  const [nameSearchStatus, setNameSearchStatus] = useState<
    'idle' | 'loading' | 'results' | 'no-results' | 'error'
  >('idle')
  const [nameSearchResults, setNameSearchResults] = useState<NpiResult[]>([])

  // Form fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [addressLine, setAddressLine] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressState, setAddressState] = useState('')
  const [addressPostalCode, setAddressPostalCode] = useState('')

  const [saving, setSaving] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [npiSearchTouched, setNpiSearchTouched] = useState(false)
  const [nameSearchTouched, setNameSearchTouched] = useState(false)

  const touch = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }))

  // Validation
  const errors = useMemo(
    () => ({
      firstName: validateName(firstName, 'First name'),
      lastName: validateName(lastName, 'Last name'),
      phone: validatePhone(phone),
      addressLine: validateAddressLine(addressLine),
      addressCity: validateCity(addressCity),
      addressState: validateState(addressState),
      addressPostalCode: validateZip(addressPostalCode),
    }),
    [firstName, lastName, phone, addressLine, addressCity, addressState, addressPostalCode],
  )

  const isFormValid = Object.values(errors).every((e) => e === null)

  // Reset / pre-populate on open
  useEffect(() => {
    if (!isOpen) return
    setNpiStatus('idle')
    setNpiFoundLabel('')
    setNameSearch('')
    setNameSearchStatus('idle')
    setNameSearchResults([])
    setTouched({})
    setNpiSearchTouched(false)
    setNameSearchTouched(false)
    if (existingPractitioner) {
      const p = existingPractitioner
      setNpi(p.identifier?.find((i) => i.system === 'http://hl7.org/fhir/sid/us-npi')?.value ?? '')
      setFirstName(p.name?.[0]?.given?.[0] ?? '')
      setLastName(p.name?.[0]?.family ?? '')
      setPhone(p.telecom?.find((t) => t.system === 'phone')?.value ?? '')
      setAddressLine(p.address?.[0]?.line?.[0] ?? '')
      setAddressCity(p.address?.[0]?.city ?? '')
      setAddressState(p.address?.[0]?.state ?? '')
      setAddressPostalCode(p.address?.[0]?.postalCode ?? '')
    } else {
      setNpi('')
      setFirstName('')
      setLastName('')
      setPhone('')
      setAddressLine('')
      setAddressCity('')
      setAddressState('')
      setAddressPostalCode('')
    }
  }, [isOpen, existingPractitioner])

  const applyResult = (result: NpiResult) => {
    const fields = npiResultToFields(result)
    setNpi(fields.npi)
    setFirstName(fields.firstName)
    setLastName(fields.lastName)
    setPhone(fields.phone)
    setAddressLine(fields.addressLine)
    setAddressCity(fields.addressCity)
    setAddressState(fields.addressState)
    setAddressPostalCode(fields.addressPostalCode)
  }

  const handleNpiLookup = async () => {
    setNpiStatus('loading')
    try {
      const result = await lookupByNpi(middleware, npi)
      if (!result) {
        setNpiStatus('not-found')
      } else if (result.basic.status !== 'A') {
        setNpi('')
        setNpiStatus('inactive')
      } else {
        applyResult(result)
        setNpiFoundLabel(npiResultLabel(result))
        setNpiStatus('found')
      }
    } catch {
      setNpiStatus('error')
    }
  }

  const handleNameSearch = async () => {
    setNameSearchStatus('loading')
    setNameSearchResults([])
    try {
      const results = await searchByName(middleware, nameSearch)
      const activeResults = results.filter((r) => r.basic.status === 'A')
      if (activeResults.length > 0) {
        setNameSearchResults(activeResults)
        setNameSearchStatus('results')
      } else {
        setNameSearchStatus('no-results')
      }
    } catch {
      setNameSearchStatus('error')
    }
  }

  const handleSave = async () => {
    setTouched(ALL_TOUCHED)
    if (!isFormValid) return

    setSaving(true)
    try {
      const { linkedToExisting } = await updatePatientPcp(middleware, {
        patientId: patient.id!,
        npi: npi.trim() || undefined,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone || undefined,
        addressLine: addressLine.trim() || undefined,
        addressCity: addressCity.trim() || undefined,
        addressState: addressState || undefined,
        addressPostalCode: addressPostalCode.trim() || undefined,
      })

      showNotification(
        linkedToExisting
          ? {
              title: 'Linked to existing provider',
              message:
                'This NPI already exists in the system. The patient has been linked to the existing provider record.',
              color: 'blue',
            }
          : {
              title: 'Saved',
              message: 'Primary Care Physician information updated.',
              color: 'green',
            },
      )
      onSuccess()
      onClose()
    } catch {
      showNotification({
        title: 'Error',
        message: 'Failed to save PCP information.',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const npiSearchDisabled = npi.length !== 10
  const nameSearchDisabled = nameSearch.trim().length < 2

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Primary Care Physician (PCP)"
      centered
      size="lg"
    >
      {/* ── NPI Lookup ── */}
      <div className="mb-1 flex items-end gap-2 px-1">
        <InputBase
          label={<span className="line-[1.4] text-[13px] text-[#7D7987]">NPI Number</span>}
          placeholder="0000000000"
          component={IMaskInput}
          mask="0000000000"
          value={npi}
          onChange={(e) => setNpi((e as React.ChangeEvent<HTMLInputElement>).currentTarget.value)}
          classNames={{ root: 'flex-1', input: 'h-[36px]' }}
          rightSection={
            npi ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Clear NPI"
                onClick={() => {
                  setNpi('')
                  setNpiStatus('idle')
                  setNpiFoundLabel('')
                  setNpiSearchTouched(false)
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          rightSectionPointerEvents={npi ? 'all' : 'none'}
        />
        <Button
          variant="outline"
          className={
            npiSearchDisabled
              ? 'cursor-not-allowed !border-gray-200 !bg-gray-200 !text-gray-500 hover:!bg-gray-200 active:!bg-gray-200'
              : '!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]'
          }
          loading={npiStatus === 'loading'}
          onClick={() => {
            setNpiSearchTouched(true)
            if (!npiSearchDisabled) handleNpiLookup()
          }}
        >
          Search
        </Button>
      </div>
      {npiSearchTouched && npiSearchDisabled && (
        <p className="mb-3 px-1 text-xs text-red-500">Enter all 10 digits to search.</p>
      )}

      {npiStatus === 'found' && (
        <Alert
          icon={<IconCheck size={16} />}
          color="green"
          className="mx-1 mb-3"
          withCloseButton
          onClose={() => setNpiStatus('idle')}
        >
          {npiFoundLabel}
        </Alert>
      )}
      {(npiStatus === 'not-found' || npiStatus === 'inactive' || npiStatus === 'error') && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          className="mx-1 mb-3"
          withCloseButton
          onClose={() => setNpiStatus('idle')}
        >
          {npiStatus === 'not-found'
            ? `No individual provider found with NPI ${npi}. Enter details manually below.`
            : npiStatus === 'inactive'
              ? 'That NPI belongs to an inactive provider. Enter details manually below.'
              : 'NPI lookup failed. Enter details manually below.'}
        </Alert>
      )}

      {/* ── Name Search ── */}
      <div className="mb-1 flex items-end gap-2 px-1">
        <TextInput
          label={<span className="line-[1.4] text-[13px] text-[#7D7987]">Search by Name</span>}
          placeholder="e.g. Jane Smith"
          value={nameSearch}
          onChange={(e) => {
            setNameSearch(e.currentTarget.value)
            if (nameSearchStatus !== 'idle') setNameSearchStatus('idle')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nameSearch.trim().length >= 2) handleNameSearch()
          }}
          classNames={{ root: 'flex-1', input: 'h-[36px]' }}
          rightSection={
            nameSearch ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Clear name search"
                onClick={() => {
                  setNameSearch('')
                  setNameSearchStatus('idle')
                  setNameSearchResults([])
                  setNameSearchTouched(false)
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          rightSectionPointerEvents={nameSearch ? 'all' : 'none'}
        />
        <Button
          variant="outline"
          className={
            nameSearchDisabled
              ? 'cursor-not-allowed !border-gray-200 !bg-gray-200 !text-gray-500 hover:!bg-gray-200 active:!bg-gray-200'
              : '!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]'
          }
          loading={nameSearchStatus === 'loading'}
          onClick={() => {
            setNameSearchTouched(true)
            if (!nameSearchDisabled) handleNameSearch()
          }}
        >
          Search
        </Button>
      </div>
      {nameSearchTouched && nameSearchDisabled && (
        <p className="mb-3 px-1 text-xs text-red-500">Enter at least 2 characters to search.</p>
      )}

      {nameSearchStatus === 'results' && (
        <Stack gap="xs" className="mx-1 mb-3 max-h-[200px] overflow-y-auto">
          {nameSearchResults.map((result) => {
            const loc =
              result.addresses.find((a) => a.address_purpose === 'LOCATION') ?? result.addresses[0]
            const specialty = result.taxonomies.find((t) => t.primary)?.desc ?? ''
            const name = [result.basic.first_name, result.basic.last_name]
              .filter((value): value is string => Boolean(value))
              .map(toTitleCase)
              .join(' ')
            const credential = result.basic.credential ? `, ${result.basic.credential}` : ''
            return (
              <Paper
                key={result.number}
                withBorder
                p="xs"
                className="flex items-center justify-between"
              >
                <div>
                  <Text size="sm" fw={500}>
                    {name}
                    {credential}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {[specialty, loc ? `${toTitleCase(loc.city)}, ${loc.state}` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  <Text size="xs" c="dimmed">
                    NPI: {result.number}
                  </Text>
                </div>
                <Button
                  size="xs"
                  variant="outline"
                  className="!text-primary !border-[#D0E1F2]"
                  onClick={() => {
                    applyResult(result)
                    setNpiFoundLabel(npiResultLabel(result))
                    setNpiStatus('found')
                    setNameSearchResults([])
                    setNameSearchStatus('idle')
                  }}
                >
                  Select
                </Button>
              </Paper>
            )
          })}
        </Stack>
      )}
      {(nameSearchStatus === 'no-results' || nameSearchStatus === 'error') && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          className="mx-1 mb-3"
          withCloseButton
          onClose={() => setNameSearchStatus('idle')}
        >
          {nameSearchStatus === 'no-results'
            ? `No providers found for "${nameSearch}". Enter details manually below.`
            : 'Name search failed. Enter details manually below.'}
        </Alert>
      )}

      {/* ── Divider ── */}
      <div className="mb-4 flex items-center gap-3 px-1">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-400">or enter manually</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      {/* ── Form fields ── */}
      <Box className="grid grid-cols-2 gap-4 px-1">
        <TextInput
          label={<span className="line-[1.4] text-[13px] text-[#7D7987]">First Name</span>}
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          onBlur={() => touch('firstName')}
          error={touched.firstName ? errors.firstName : undefined}
          classNames={{ root: 'w-full', input: 'h-[36px] border border-slate-300' }}
        />
        <TextInput
          label={<span className="line-[1.4] text-[13px] text-[#7D7987]">Last Name</span>}
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          onBlur={() => touch('lastName')}
          error={touched.lastName ? errors.lastName : undefined}
          classNames={{ root: 'w-full', input: 'h-[36px] border border-slate-300' }}
        />

        <div className="col-span-2">
          <p
            className={`text-light mb-[2px] text-xs font-semibold ${touched.phone && errors.phone ? 'text-red-500' : ''}`}
          >
            Phone
          </p>
          <USPhoneInput
            placeholder="eg: 234 567 8900"
            value={phone}
            onChange={(value) => setPhone(value || '')}
            onBlur={() => touch('phone')}
            error={touched.phone && errors.phone ? errors.phone : undefined}
          />
        </div>

        <AddressAutocomplete
          label="Address Line"
          placeholder="123 Highland Rd"
          value={addressLine}
          onChange={setAddressLine}
          onAddressSelect={(suggestion) => {
            const line = [suggestion.street_line, suggestion.secondary].filter(Boolean).join(' ')
            const matchingState =
              states.find(
                (s) =>
                  s.label.toLowerCase() === suggestion.state.toLowerCase() ||
                  s.value.toLowerCase() === suggestion.state.toLowerCase(),
              )?.value || suggestion.state
            setAddressLine(line)
            setAddressCity(suggestion.city)
            setAddressState(matchingState)
            setAddressPostalCode(suggestion.zipcode ?? '')
          }}
          onBlur={() => touch('addressLine')}
          error={touched.addressLine ? errors.addressLine : undefined}
        />

        <TextInput
          label={<span className="line-[1.4] text-[13px] text-[#7D7987]">City</span>}
          placeholder="City"
          value={addressCity}
          onChange={(e) => setAddressCity(e.currentTarget.value)}
          onBlur={() => touch('addressCity')}
          error={touched.addressCity ? errors.addressCity : undefined}
          classNames={{ root: 'w-full', input: 'h-[36px] border border-slate-300' }}
        />

        <Select
          label="State/Province"
          placeholder="Select state"
          data={states}
          value={addressState || null}
          onChange={(v) => setAddressState(v || '')}
          onBlur={() => touch('addressState')}
          error={touched.addressState ? errors.addressState : undefined}
          rightSection={<IconChevronDown size={25} className="mr-[5px] text-gray-400" />}
          rightSectionWidth={30}
          classNames={{
            root: 'w-full',
            label: 'line-[1.5] text-[#7D7987] font-[13px]',
            input: 'h-[36px]',
          }}
        />

        <InputBase
          label="ZIP Code"
          placeholder="00000 or 12345-1234"
          component={IMaskInput}
          mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
          value={addressPostalCode}
          onChange={(e) =>
            setAddressPostalCode((e as React.ChangeEvent<HTMLInputElement>).currentTarget.value)
          }
          onBlur={(e) => {
            const val = e.currentTarget.value.replace(/\D/g, '')
            if (val.length === 9) setAddressPostalCode(`${val.slice(0, 5)}-${val.slice(5)}`)
            touch('addressPostalCode')
          }}
          error={touched.addressPostalCode ? errors.addressPostalCode : undefined}
          classNames={{
            label: 'line-[1.5] text-[#7D7987] font-[13px]',
            input: 'h-[36px]',
          }}
        />
      </Box>

      <Group justify="flex-end" mt="md" className="px-1">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          className={
            saving
              ? undefined
              : !isFormValid
                ? 'cursor-not-allowed !border-gray-200 !bg-gray-200 !text-gray-500 hover:!bg-gray-200 active:!bg-gray-200'
                : '!bg-primary'
          }
        >
          Save
        </Button>
      </Group>
    </Modal>
  )
}
